// Placeholder publish-date logic for blog posts.
//
// If the SEO API ever returns null for every date field (createdAt, approvedAt, publishedAt,
// updatedAt) on a post, we derive a stable placeholder from that post's position in the API
// list, which is returned oldest -> newest. The newest post gets ANCHOR, and each older post is
// dated one week earlier. The anchor is fixed (not "now") so a post's date does not drift
// forward every day.
//
// Whenever the backend returns a real date on any of the fields above, that real value is used
// instead of the placeholder.
//
// Ported from NevTan Sign's src/utils/blogDates.js, with the anchor moved to today's date so
// synthesized placeholder dates for undated `nevtan_ai` posts aren't backdated.
import type { BlogPost } from './blogApi'

export const BLOG_DATE_ANCHOR = '2026-09-18'
const WEEK_MS = 7 * 24 * 60 * 60 * 1000

/** The real publish date from whichever field the API provides, or null. */
export function realPublishedDate(page?: BlogPost | null): string | null {
  return page?.approvedAt || page?.publishedAt || page?.createdAt || page?.updatedAt || null
}

/** Synthesize a date from a post's index within the full list (oldest -> newest). */
export function synthesizeDateByIndex(
  index: number | null | undefined,
  total: number | null | undefined,
): string | null {
  if (index == null || index < 0 || !total) return null
  const weeksBack = total - 1 - index
  const anchor = new Date(`${BLOG_DATE_ANCHOR}T00:00:00Z`)
  return new Date(anchor.getTime() - weeksBack * WEEK_MS).toISOString()
}

/**
 * Resolve the display date for a page: the real date if present, otherwise a placeholder
 * derived from its position in `allPages` (matched by slug so it works whether `page` came from
 * the list request or a single-page request).
 */
export function resolvePublishedDate(
  page: BlogPost | null | undefined,
  allPages: BlogPost[] | null | undefined,
): string | null {
  const real = realPublishedDate(page)
  if (real) return real
  if (!page || !Array.isArray(allPages) || allPages.length === 0) return null
  const idx = allPages.findIndex((p) => p.slug === page.slug)
  return synthesizeDateByIndex(idx, allPages.length)
}
